import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { firstValueFrom } from 'rxjs';

import { type FormInstance, FormInstanceFactory, type ResolvedFormTemplateResponseDto } from '@forthbridge/forms';

import { ErrorHandlerService } from '@core/errors/error-handler.service';
import { BaseActionHandlerService } from '@core/services/action-handler';
import { ContentService } from '@core/services/content.service';
import { ModalService } from '@core/services/modal.service';
import { OrganizationService } from '@core/services/organization.service';

import type { ComponentAction, ComponentGroup } from '@shared/models/dynamic-layout.types';
import {
  type ErrorDisplayConfig,
  UniversalModalTemplate,
} from '@shared/universal-modal-template/universal-modal-template';
import type { UniversalModalContent } from '@shared/universal-modal-template/universal-modal-template.types';
import { DataState } from '@shared/utils/data-state';

import { ProgramDeliveryService } from '@swagger-services/program/api/programDelivery.service';

import type { AddNewShippingAddressModalContent } from './add-new-shipping-address-modal.types';

interface ShippingAddressModalContent extends UniversalModalContent {
  formConfig?: ResolvedFormTemplateResponseDto;
  groups?: ComponentGroup[];
}

@Component({
  selector: 'app-add-new-shipping-address-modal',
  templateUrl: './add-new-shipping-address.modal.html',
  imports: [UniversalModalTemplate],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ContentService],
})
export class AddNewShippingAddressModal {
  private readonly modalService = inject(ModalService);
  private readonly errorHandlerService = inject(ErrorHandlerService);
  private readonly contentService = inject(ContentService<ShippingAddressModalContent>);
  private readonly programDeliveryService = inject(ProgramDeliveryService);
  private readonly organizationService = inject(OrganizationService);
  private readonly baseActionHandler = inject(BaseActionHandlerService);
  private readonly formInstanceFactory = inject(FormInstanceFactory);
  private readonly destroyRef = inject(DestroyRef);

  private readonly actionHandler = this.baseActionHandler.createScope({
    close: () => this.modalService.dismissLatest(),
    retry: () => this.error.set(null),
    save: () => this.handleSave(),
  });

  readonly content = input<AddNewShippingAddressModalContent>();
  readonly programId = input.required<string>();

  protected readonly templateContent = this.contentService.rawContent;

  private readonly _formInstance = signal<FormInstance | undefined>(undefined);
  protected readonly formInstance = this._formInstance.asReadonly();

  protected readonly isSubmitting = signal<boolean>(false);
  protected readonly isSubmitted = signal<boolean>(false);
  protected readonly formInvalid = signal<boolean>(true);
  protected readonly error = signal<ErrorDisplayConfig | null>(null);

  protected readonly state = computed(() => {
    const template = this.templateContent();
    if (template) return DataState.success(template);
    return DataState.loading<ShippingAddressModalContent>();
  });

  protected readonly groups = computed<ComponentGroup[]>(() => {
    return this.templateContent()?.groups ?? [];
  });

  protected readonly contentLabels = computed(() => {
    const templateContent = this.templateContent()?.content as AddNewShippingAddressModalContent | undefined;
    const contentOverrides = this.content();

    return {
      ...templateContent,
      ...contentOverrides,
    };
  });

  protected readonly submittingText = computed(() => this.contentLabels().submittingText);

  protected readonly submittedText = computed(() => this.contentLabels().submittedText);

  constructor() {
    afterNextRender(() => {
      this.contentService.loadModalContent('addNewShippingAddress').catch(console.error);
    });

    effect(() => {
      const content = this.contentService.rawContent();
      if (content?.formConfig && !this._formInstance()) {
        this.createFormInstance(content.formConfig).catch(console.error);
      }
    });
  }

  private async createFormInstance(formConfig: ResolvedFormTemplateResponseDto): Promise<void> {
    if (this._formInstance()) return;

    const instance = await firstValueFrom(this.formInstanceFactory.createFromConfig(formConfig, this.destroyRef));
    this._formInstance.set(instance);

    const form = instance.getForm();
    this.formInvalid.set(form.invalid);

    form.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.formInvalid.set(form.invalid);
    });
  }

  protected onAction(action?: ComponentAction): void {
    if (action) {
      this.actionHandler.execute(action);
    }
  }

  private async handleSave(): Promise<void> {
    const form = this.formInstance()?.getForm();
    if (!form || form.invalid) return;

    this.isSubmitting.set(true);
    this.error.set(null);

    const formValue = form.getRawValue();
    const organizationId = this.organizationService.organizationId() ?? '';

    try {
      await this.programDeliveryService.programDeliveryControllerCheckDeliveryAddress(organizationId, {
        address: formValue.address!,
        address2: formValue.address2 || undefined,
        cityTown: formValue.cityTown!,
        state: formValue.state,
        zip: formValue.zip!,
      });

      const newAddress = await this.programDeliveryService.programDeliveryControllerCreate(
        organizationId,
        this.programId(),
        {
          address: formValue.address!,
          address2: formValue.address2 || undefined,
          cityTown: formValue.cityTown!,
          state: formValue.state,
          zip: formValue.zip!,
          isDefault: true,
        },
      );

      this.isSubmitting.set(false);
      this.isSubmitted.set(true);

      setTimeout(() => {
        this.modalService.dismissLatest({ createdAddress: newAddress });
      }, 1500);
    } catch (error: unknown) {
      this.isSubmitting.set(false);
      this.error.set(this.errorHandlerService.getFeedbackConfig(error));
    }
  }
}
